import type { HttpMethod } from "@/auth/permissions/permission-targets";

import type { NavItem, NavSection } from "./nav-items";

export type ResolvedNavItem = NavItem & {
  /** planned 項目。表示するがリンクとして機能しない */
  readonly disabled: boolean;
  readonly children?: readonly ResolvedNavItem[];
};

export type ResolvedNavSection = {
  readonly titleKey: NavSection["titleKey"];
  readonly items: readonly ResolvedNavItem[];
};

export type CanFn = (_uri: string, _method: HttpMethod) => boolean;

/**
 * 表示すべきナビ項目を決める。分岐ロジックをコンポーネントに書かないための純関数。
 *
 * - hidden は除外
 * - requires を 1 つでも満たさなければ除外（AND）
 * - planned は disabled: true で残す
 * - children を再帰処理し、children が全滅して to も無い親は除外
 */
export function resolveNavItems(
  items: readonly NavItem[],
  can: CanFn,
): readonly ResolvedNavItem[] {
  return items.flatMap((item) => {
    if (item.status === "hidden") return [];
    if (!satisfiesRequirements(item, can)) return [];

    const children =
      item.children === undefined
        ? undefined
        : resolveNavItems(item.children, can);

    // 子だけを束ねるグループで、子が全部消えたなら親も出さない
    if (
      children !== undefined &&
      children.length === 0 &&
      item.to === undefined
    ) {
      return [];
    }

    return [{ ...item, disabled: item.status === "planned", children }];
  });
}

/** セクション単位の解決。項目が全滅したセクションは落とす。 */
export function resolveNavSections(
  sections: readonly NavSection[],
  can: CanFn,
): readonly ResolvedNavSection[] {
  return sections.flatMap((section) => {
    const items = resolveNavItems(section.items, can);
    return items.length === 0 ? [] : [{ titleKey: section.titleKey, items }];
  });
}

function satisfiesRequirements(item: NavItem, can: CanFn): boolean {
  return (item.requires ?? []).every(({ uri, method }) => can(uri, method));
}
